import React, { useState } from 'react';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Send, Mail, Info } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function BulkInvitation() {
  const [emails, setEmails] = useState('');
  const [customSubject, setCustomSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const parseEmails = (text) => {
    // Split by newlines, commas, or semicolons and clean up
    return text
      .split(/[\n,;]+/)
      .map(e => e.trim())
      .filter(e => e.length > 0);
  };

  const emailCount = parseEmails(emails).length;

  const handleSend = async () => {
    const emailList = parseEmails(emails);
    
    if (emailList.length === 0) {
      toast.error('Please enter at least one email address');
      return;
    }

    setSending(true);
    setResult(null);
    
    try {
      const response = await api.post('/admin/send-bulk-invitation', {
        emails: emailList,
        subject: customSubject || null
      });
      
      setResult(response.data);
      toast.success(`Invitations sent to ${response.data.sent_count} recipients!`);
      
      // Clear form on success
      setEmails('');
      setCustomSubject('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitations');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <div className="flex-1 p-8 ml-64">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground">Send Profile Invitations</h1>
            <p className="text-stone-600 mt-1">Invite people to register for a free bookshelf from The Bookshelf Project</p>
          </div>

          <Card className="border-stone-200 shadow-card mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Bulk Email Invitation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">How it works:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Enter email addresses (one per line, or separated by commas)</li>
                      <li>All recipients will be BCC'd (blind copied) for privacy</li>
                      <li>They'll receive an invitation to register for a free bookshelf</li>
                      <li>After registering, they'll appear in "Pending Users" for your approval</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="emails">Email Addresses *</Label>
                <Textarea
                  id="emails"
                  placeholder="Enter email addresses, one per line or separated by commas:&#10;&#10;example1@email.com&#10;example2@email.com&#10;example3@email.com"
                  value={emails}
                  onChange={(e) => setEmails(e.target.value)}
                  className="h-48 mt-2 font-mono text-sm"
                  data-testid="emails-input"
                />
                <p className="text-sm text-stone-500 mt-1">
                  {emailCount} email{emailCount !== 1 ? 's' : ''} detected
                </p>
              </div>

              <div>
                <Label htmlFor="subject">Custom Subject Line (optional)</Label>
                <Input
                  id="subject"
                  placeholder="Default: You're Invited to Receive a Free Bookshelf! 📚"
                  value={customSubject}
                  onChange={(e) => setCustomSubject(e.target.value)}
                  className="mt-2"
                  data-testid="subject-input"
                />
              </div>

              <Button
                onClick={handleSend}
                disabled={sending || emailCount === 0}
                className="w-full h-12 text-lg"
                data-testid="send-invitations-button"
              >
                {sending ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send Invitations to {emailCount} Recipient{emailCount !== 1 ? 's' : ''}
                  </>
                )}
              </Button>

              {result && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 font-medium">
                    ✅ Successfully sent invitations to {result.sent_count} recipient{result.sent_count !== 1 ? 's' : ''}!
                  </p>
                  {result.invalid_emails && result.invalid_emails.length > 0 && (
                    <p className="text-amber-700 text-sm mt-2">
                      ⚠️ Invalid emails skipped: {result.invalid_emails.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-stone-200 shadow-card">
            <CardHeader>
              <CardTitle>Email Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-stone-50 rounded-lg p-6 border">
                <p className="text-sm text-stone-500 mb-2">Subject:</p>
                <p className="font-medium mb-4">{customSubject || "You're Invited to Receive a Free Bookshelf! 📚"}</p>
                
                <p className="text-sm text-stone-500 mb-2">Preview:</p>
                <div className="bg-white rounded-lg p-4 border text-sm">
                  <h3 className="text-lg font-bold text-green-800 mb-2">You're Invited to Receive a Free Bookshelf! 📚</h3>
                  <p className="mb-3">Hello!</p>
                  <p className="mb-3">You are invited to register for a <strong>free bookshelf</strong> from <strong>The Bookshelf Project</strong>. We're building in-home libraries for children across DC, Maryland, and Virginia!</p>
                  
                  <div className="bg-green-50 border border-green-200 rounded p-3 my-4">
                    <p className="font-medium text-green-800 mb-2">🎁 What You'll Receive:</p>
                    <ul className="list-disc list-inside text-green-700 text-sm space-y-1">
                      <li>A <strong>free bookshelf</strong> for your home</li>
                      <li><strong>Age-appropriate books</strong> for your children</li>
                      <li><strong>Free delivery</strong> right to your door</li>
                    </ul>
                  </div>
                  
                  <p className="mb-3 font-medium">Use the link below to create your profile and get started:</p>
                  
                  <div className="text-center my-4">
                    <span className="inline-block bg-green-700 text-white px-6 py-2 rounded-full font-medium">
                      Create Your Profile
                    </span>
                  </div>
                  
                  <p className="font-medium text-green-800 mb-2">How It Works:</p>
                  <ol className="list-decimal list-inside text-stone-700 space-y-1 mb-4">
                    <li>Click the button to access our registration portal</li>
                    <li>Complete your family profile</li>
                    <li>Wait for approval from our team</li>
                    <li>Schedule your delivery</li>
                    <li>Receive your bookshelf!</li>
                  </ol>
                  
                  <p className="text-stone-600 text-xs italic">Building In-Home Libraries for Children</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
